import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule } from 'ng-apexcharts';
import { SharedToolsService } from '@huge-monorepo/shared/tools';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { ActivatedRoute } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { registerLocaleData } from '@angular/common';
// import { MY_FORMATS } from '@huge-monorepo/hip/tools';
import { SharedTeltonikaSingletonService } from '@huge-monorepo/shared/services';
import { IHIPTeltonikaService, ICustomerTeltonikaService, TeltonikaDevicesWANData, TeltonikaDeviceWANUsage } from '@huge-monorepo/shared/interfaces';
import { Moment } from 'moment';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { NavigationService } from '@huge-monorepo/portal/services';
import { Subscription } from 'rxjs';
import { TeltonikaSignalStrengthForPeriod } from '../../../../interfaces/TeltonikaSignalStrengthForPeriod';

const SWEDISH_FORMAT = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'MM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MM YYYY',
  },
};
@Component({
  selector: 'huge-monorepo-teltonika-device-metrics',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, MaterialModule],
  templateUrl: './teltonika-device-metrics.component.html',
  styleUrl: './teltonika-device-metrics.component.scss',
  providers: [{ provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
  { provide: MAT_DATE_FORMATS, useValue: SWEDISH_FORMAT },],
})
export class TeltonikaDeviceMetricsComponent implements OnInit {
  teltonikaService = this.sharedTeltonikaService.getService();
  chartOptions: any;
  signalStrengthChartOptions: any;
  sim1DailyUsage: Map<string, { Tx: number | null; Rx: number | null }>;
  sim2DailyUsage: Map<string, { Tx: number | null; Rx: number | null }>;
  signalStrengthData: TeltonikaSignalStrengthForPeriod[];

  sim1Usage: DataUsage[];
  sim2Usage: DataUsage[];
  sim1TotalTx: number = 0;
  sim1TotalRx: number = 0;
  sim2TotalTx: number = 0;
  sim2TotalRx: number = 0;
  endDate: Date | null = new Date();
  startDate: Date | null = new Date();
  id: number;
  displaySpinner: boolean = true;
  errorOnLoading: boolean = false;
  sidenavObservable: Subscription;
  signalStrengthSidenavObservable: Subscription;
  isSideNavOpen: boolean;

  displaySignalStrengthSpinner: boolean = true;
  errorOnSignalStrengthLoading: boolean = false;
  signalStrengthStartDate: Date | null = new Date();
  signalStrengthEndDate: Date | null = new Date();

  wanData: TeltonikaDevicesWANData | null = null;
  wanDataLoading: boolean = true;
  errorInWanData: boolean = false;

  wanChartOptions: any;
  wanUsageData: { [date: string]: TeltonikaDeviceWANUsage } | null = null;
  wanUsageLoading: boolean = true;
  errorInWanUsage: boolean = false;
  wanUsageStartDate: Date | null = new Date();
  wanUsageEndDate: Date | null = new Date();

  constructor(
    private tools: SharedToolsService,
    private sharedTeltonikaService: SharedTeltonikaSingletonService<ICustomerTeltonikaService | IHIPTeltonikaService>,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
  ) { }

  ngOnInit(): void {
    // Metrics graph Start logic
    if (!this.startDate) {
      this.startDate = new Date();
    }
    this.startDate.setMonth(this.startDate.getMonth() - 1);

    // Signal Strength graph Start logic
    if (!this.signalStrengthStartDate) {
      this.signalStrengthStartDate = new Date();
    }
    this.signalStrengthStartDate.setDate(this.signalStrengthStartDate.getDate() - 7);
    if (!this.wanUsageStartDate) {
      this.wanUsageStartDate = new Date();
    }
    this.wanUsageStartDate.setDate(this.wanUsageStartDate.getDate() - 1);

    this.activatedRoute.paramMap.subscribe(
      (paraMap: any) => {
        if (paraMap.params.id) {
          let id = paraMap.params.id;
          this.id = id;
          this.fetchData(id);
          this.fetchSignalStrengthForPeriod(id);
          this.getDevicesWANData(id);
          this.fetchWANUsageData(id);
        }
      }
    );
  }

  updateStart(event: MatDatepickerInputEvent<Moment>): void {
    if (event.value == null) {
      this.startDate = null;
      return;
    }
    let date = event.value.toDate();
    date.setHours(0, 0, 0, 0);
    this.startDate = date;
  }

  updateEnd(event: MatDatepickerInputEvent<Moment>): void {
    if (event.value == null) {
      this.endDate = null;
      return;
    }
    let date = event.value.toDate();
    date.setHours(23, 59, 59, 999);
    this.endDate = date;
  }

  updateWANUsageStart(event: MatDatepickerInputEvent<Moment>): void {
    if (event.value == null) {
      this.wanUsageStartDate = null;
      return;
    }
    let date = event.value.toDate();
    date.setHours(0, 0, 0, 0);
    this.wanUsageStartDate = date;
  }

  updateWANUsageEnd(event: MatDatepickerInputEvent<Moment>): void {
    if (event.value == null) {
      this.wanUsageEndDate = null;
      return;
    }
    let date = event.value.toDate();
    date.setHours(23, 59, 59, 999);
    this.wanUsageEndDate = date;
  }

  updateGraph(): void {
    this.displaySpinner = true;
    this.fetchData(this.id);
  }

  fetchData(id: number): void {
    if (this.startDate == null || this.endDate == null) {
      return;
    }
    this.teltonikaService.getDataUsagePerSim(id, this.startDate, this.endDate, 0).subscribe({
      next: (data) => {
        const groupedData = new Map<string, { Tx: number | null; Rx: number | null }>();

        for (const elem of data['Usage']) {
          const date = new Date(elem.Timestamp).toISOString().split('T')[0];
          if (!groupedData.has(date)) {
            groupedData.set(date, { Tx: null, Rx: null });
          }

          const dataForDate = groupedData.get(date)!;

          if (elem.Detail != null) {
            if (dataForDate.Tx === null && elem.Detail.Tx != null) {
              this.sim1TotalTx += +(elem.Detail.Tx);
              dataForDate.Tx = +(elem.Detail.Tx);
            } else if (dataForDate.Tx !== null && elem.Detail.Tx != null) {
              this.sim1TotalTx += +(elem.Detail.Tx);
              dataForDate.Tx += +(elem.Detail.Tx);
            }

            if (dataForDate.Rx === null && elem.Detail.Rx != null) {
              this.sim1TotalRx += +(elem.Detail.Rx);
              dataForDate.Rx = +(elem.Detail.Rx);
            } else if (dataForDate.Rx !== null && elem.Detail.Rx != null) {
              this.sim1TotalRx += +(elem.Detail.Rx);
              dataForDate.Rx += +(elem.Detail.Rx);
            }
          }
        }

        this.sim1TotalTx = this.tools.BytesConversion(this.sim1TotalTx, 'mb');
        this.sim1TotalRx = this.tools.BytesConversion(this.sim1TotalRx, 'mb');
        this.sim1DailyUsage = groupedData;


        // Do call for second sim
        // TODO: probably should be done in parallel, try something like combineLatest
        this.teltonikaService.getDataUsagePerSim(id, this.startDate, this.endDate, 1).subscribe({
          next: (data) => {
            const groupedData = new Map<string, { Tx: number | null; Rx: number | null }>();

            for (const elem of data['Usage']) {
              const date = new Date(elem.Timestamp).toISOString().split('T')[0];
              if (!groupedData.has(date)) {
                groupedData.set(date, { Tx: null, Rx: null });
              }

              const dataForDate = groupedData.get(date)!;

              if (elem.Detail != null) {
                if (dataForDate.Tx === null && elem.Detail.Tx != null) {
                  this.sim2TotalTx += +(elem.Detail.Tx);
                  dataForDate.Tx = +(elem.Detail.Tx);
                } else if (dataForDate.Tx !== null && elem.Detail.Tx != null) {
                  this.sim2TotalTx += +(elem.Detail.Tx);
                  dataForDate.Tx += +(elem.Detail.Tx);
                }

                if (dataForDate.Rx === null && elem.Detail.Rx != null) {
                  this.sim2TotalRx += +(elem.Detail.Rx);
                  dataForDate.Rx = +(elem.Detail.Rx);
                } else if (dataForDate.Rx !== null && elem.Detail.Rx != null) {
                  this.sim2TotalRx += +(elem.Detail.Rx);
                  dataForDate.Rx += +(elem.Detail.Rx);
                }
              }
            }

            this.sim2TotalTx = this.tools.BytesConversion(this.sim2TotalTx, 'mb');
            this.sim2TotalRx = this.tools.BytesConversion(this.sim2TotalRx, 'mb');
            this.sim2DailyUsage = groupedData;

          },
          error: (error) => {
            console.error(error);
          },
          complete: () => {
            this.setChartOptions();
            this.displaySpinner = false;
            this.errorOnLoading = false;
          }
        });
      },
      error: (error) => {
        console.error(error);
        this.displaySpinner = false;
        this.errorOnLoading = true;
      },
    });

  }

  setChartOptions() {
    const sim1Data = Array.from(this.sim1DailyUsage.values());
    let sim1Tx: (number | null)[] = [];
    let sim1Rx: (number | null)[] = [];
    sim1Data.forEach((elem) => {
      if (elem == null) {
        sim1Tx.push(null);
        sim1Rx.push(null);
      }
      if (elem.Tx != null)
        sim1Tx.push(this.tools.BytesConversion(elem.Tx || 0, 'mb'));
      else
        sim1Tx.push(null);

      if (elem.Rx != null)
        sim1Rx.push(this.tools.BytesConversion(elem.Rx || 0, 'mb'));
      else
        sim1Rx.push(null);
    });

    const sim2Data = Array.from(this.sim2DailyUsage.values());
    let sim2Tx: (number | null)[] = [];
    let sim2Rx: (number | null)[] = [];

    sim2Data.forEach((elem) => {
      if (elem == null) {
        sim2Tx.push(null);
        sim2Rx.push(null);
      }
      if (elem.Tx != null)
        sim2Tx.push(this.tools.BytesConversion(elem.Tx || 0, 'mb'));
      else
        sim2Tx.push(null);

      if (elem.Rx != null)
        sim2Rx.push(this.tools.BytesConversion(elem.Rx || 0, 'mb'));
      else
        sim2Rx.push(null);
    });

    this.chartOptions = {
      dataLabels: {
        enabled: false
      },
      series: [
        {
          name: "Sim 1 Tx",
          data: sim1Tx
        },
        {
          name: "Sim 1 Rx",
          data: sim1Rx
        },
        {
          name: "Sim 2 Tx",
          data: sim2Tx
        },
        {
          name: "Sim 2 Rx",
          data: sim2Rx
        },
      ],
      chart: {
        height: 350,
        type: "bar",
        toolbar: {
          tools: {
            download: '<div style="width: 150px;display: flex;"><span>Download Graph</span> <i class="material-icons">download</i></div>'
          }
        },
        events: {
          mounted: (chart) => {
            chart.windowResizeHandler();
          },
        },
      },
      title: {
        text: "Device Usage By Sim (MB)",
        align: "left"
      },
      xaxis: {
        categories: Array.from(this.sim1DailyUsage.keys())
      },
    };

    this.sidenavObservable = this.navigationService.GetSideNav((data) => {
      if (this.displaySpinner != true && this.isSideNavOpen != data) {
        this.isSideNavOpen = data;
        let temp = this.displaySpinner;
        this.displaySpinner = true;
        setTimeout(() => {
          this.displaySpinner = temp;
        });
      }
    });
  }

  updateSignalStrengthGraph(): void {
    this.displaySignalStrengthSpinner = true;
    this.fetchSignalStrengthForPeriod(this.id);
  }

  updateSignalStrengthStart(event: MatDatepickerInputEvent<Moment>): void {
    if (event.value == null) {
      this.signalStrengthStartDate = null;
      return;
    }
    let date = event.value.toDate();
    date.setHours(0, 0, 0, 0);
    this.signalStrengthStartDate = date;
  }

  updateSignalStrengthEnd(event: MatDatepickerInputEvent<Moment>): void {
    if (event.value == null) {
      this.signalStrengthEndDate = null;
      return;
    }
    let date = event.value.toDate();
    date.setHours(23, 59, 59, 999);
    this.signalStrengthEndDate = date;
  }

  fetchSignalStrengthForPeriod(id: number): void {
    if (this.startDate == null || this.endDate == null) {
      return;
    }
    this.teltonikaService.getTeltonikaSignalStrengthForPeriod(+id, this.signalStrengthStartDate, this.signalStrengthEndDate).subscribe({
      next: (data) => {
        this.signalStrengthData = data;
      },
      error: (error) => {
        console.error(error);
        this.displaySignalStrengthSpinner = false;
        this.errorOnSignalStrengthLoading = true;
      },
      complete: () => {
        this.setSignalStrengthChartOptions();
        this.displaySignalStrengthSpinner = false;
        this.errorOnSignalStrengthLoading = false;
      }
    });
  }

  setSignalStrengthChartOptions() {
    // We can assume dates will be in order as API applies ASC sort
    const mtnData: number[] = [];
    const vodacomData: number[] = [];
    const otherData: number[] = [];
    const categories: string[] = [];

    if (this.signalStrengthData != null) {
      this.signalStrengthData.forEach((elem: TeltonikaSignalStrengthForPeriod) => {
        if (elem.ServiceProvider != null) {
          if (elem.ServiceProvider.toLowerCase().includes('mtn')) {
            mtnData.push(this.gsmSignalStrengthConverter(+elem.SignalStrength));
          } else {
            mtnData.push(0);
          }
          if (elem.ServiceProvider.toLowerCase().includes('vodacom')) {
            vodacomData.push(this.gsmSignalStrengthConverter(+elem.SignalStrength));
          } else {
            vodacomData.push(0);
          }
        } else {
          otherData.push(this.gsmSignalStrengthConverter(+elem.SignalStrength));
        }
        categories.push(this.tools.FormatDateTimeFromDateSwedish(this.tools.ParseAPIDateWithTimezone(elem.Timestamp)));
      });
    }

    let includesSimData: boolean = false;
    let includesOtherData: boolean = false;

    if (mtnData.length > 0 || vodacomData.length > 0) {
      includesSimData = true;
    }

    if (otherData.length > 0) {
      includesOtherData = true;
    }

    this.signalStrengthChartOptions = {
      chart: {
        type: 'area',
        height: '500px',
      },
      plotOptions: {},
      series: [
        ...(includesSimData ? [{
          name: 'MTN',
          data: mtnData
        },
        {
          name: 'Vodacom',
          data: vodacomData
        }] : []),
        ...(includesOtherData ? [{
          name: 'Other',
          data: otherData
        }] : []),
      ],
      xaxis: {
        categories: categories,
        labels: {
          rotate: -45,
          rotateAlways: true,
          // formatter: function(value: string) {
          //   // Skip empty values
          //   if (!value) return '';
          //
          //   try {
          //     const currentDate = value.split(' ')[0];
          //     const previousDate = prevLabel ? prevLabel.split(' ')[0] : '';
          //
          //     prevLabel = value;
          //     return currentDate !== previousDate ? currentDate : '';
          //   } catch {
          //     return '';
          //   }
          // },
          style: {
            fontSize: '12px'
          },
        },
        // tickAmount: 10,
      },
      yaxis: {
        min: 0,
        max: 100
      },
      title: {
        text: "Signal Strength (as Percentage)",
        align: "left"
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 1,
        curve: 'straight',
      },
      colors: [
        ...(includesSimData ? ['#FFCC08', '#E00201'] : []),
        ...(includesOtherData ? ['#A2A4FF'] : []),
      ]
    }

    this.signalStrengthSidenavObservable = this.navigationService.GetSideNav((data) => {
      if (this.displaySignalStrengthSpinner != true && this.isSideNavOpen != data) {
        this.isSideNavOpen = data;
        let temp = this.displaySignalStrengthSpinner;
        this.displaySignalStrengthSpinner = true;
        setTimeout(() => {
          this.displaySignalStrengthSpinner = temp;
        });
      }
    });
  }

  gsmSignalStrengthConverter(dbm: number): number {
    const minDbm = -120.0; // Worst case
    const maxDbm = -36.0;  // Best case

    // Clamp the input dBm
    if (dbm < minDbm) {
      dbm = minDbm;
    }
    if (dbm > maxDbm) {
      dbm = maxDbm;
    }

    // Normalize to a 0–1 scale
    const normalized = (dbm - minDbm) / (maxDbm - minDbm);

    // Apply cubic sensitivity curve
    const sensitive = Math.pow(normalized, 3);

    // Convert to percentage
    const percentage = sensitive * 100.0;

    // Round to 2 decimal places
    return Math.round(percentage * 100) / 100;
  }

  getDevicesWANData(id: number) {
    this.wanDataLoading = true;
    this.errorInWanData = false;
    this.teltonikaService.getTeltonikaDeviceWANData(id).subscribe({
      next: (data: TeltonikaDevicesWANData) => {
        this.wanData = data;
        this.wanDataLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.wanDataLoading = false;
        this.errorInWanData = true;
      },
    });
  }

  convertFromSecondsToDHMS(input: number) {
    const rounded = Math.round(input);
    const days = Math.floor(rounded / (24 * 3600));
    const hours = Math.floor((rounded % (24 * 3600)) / 3600);
    const minutes = Math.floor((rounded % 3600) / 60);
    const seconds = rounded % 60;
    let output = "";
    if (days > 0) {
      output += `${days} d `;
    }
    if (hours > 0) {
      output += `${hours} h `;
    }
    if (minutes > 0) {
      output += `${minutes} m `;
    }
    output += `${seconds} s`;
    return output;
  }

  roundToThreeDecimalPlaces(value: number): number {
    return Math.round((value / 1000 / 1000) * 1000) / 1000;
  }

  fetchWANUsageData(id: number) {
    this.wanUsageLoading = true;
    this.errorInWanUsage = false;

    this.teltonikaService.getTeltonikaDeviceWANUsage(id, this.wanUsageStartDate, this.wanUsageEndDate).subscribe({
      next: (data: { [date: string]: TeltonikaDeviceWANUsage }) => {
        this.wanUsageData = data;
        this.setWANChartOptions();
        this.wanUsageLoading = false;

      },
      error: (error) => {
        console.error(error);
        this.wanUsageLoading = false;
        this.errorInWanUsage = true;
      },
    });
  }

  setWANChartOptions() {
    if (this.wanUsageData == null) {
      return;
    }
    const wanData = Object.values(this.wanUsageData);
    let tx: (number | null)[] = [];
    let rx: (number | null)[] = [];
    wanData.forEach((elem) => {
      if (elem == null) {
        tx.push(null);
        rx.push(null);
      }
      if (elem.Tx != null)
        tx.push(this.roundToThreeDecimalPlaces(elem.Tx) || 0);
      else
        tx.push(null);

      if (elem.Rx != null)
        rx.push(this.roundToThreeDecimalPlaces(elem.Rx) || 0);
      else
        rx.push(null);
    });

    this.wanChartOptions = {
      dataLabels: {
        enabled: false
      },
      series: [
        {
          name: "Tx (MB)",
          data: tx
        },
        {
          name: "Rx (MB)",
          data: rx
        },
      ],
      chart: {
        height: 350,
        type: "line",
        toolbar: {
          tools: {
            download: '<div style="width: 150px;display: flex;"><span>Download Graph</span> <i class="material-icons">download</i></div>'
          }
        },
        events: {
          mounted: (chart) => {
            chart.windowResizeHandler();
          },
        },
      },
      title: {
        text: "Device WAN Usage (MB)",
        align: "left"
      },
      xaxis: {
        categories: Object.keys(this.wanUsageData).map((date: string) => { return date.replace('T', ' ').replace('Z', ''); }),
      },
    };

    this.sidenavObservable = this.navigationService.GetSideNav((data) => {
      if (this.wanUsageLoading != true && this.isSideNavOpen != data) {
        this.isSideNavOpen = data;
        let temp = this.wanUsageLoading;
        this.wanUsageLoading = true;
        setTimeout(() => {
          this.wanUsageLoading = temp;
        });
      }
    });
  }
}


interface DataUsage {
  Time: string;
  Detail: DataUsageDetails | null;
}

interface DataUsageDetails {
  Tx: number;
  Rx: number;
}
